/**
 * Runtime resolver for the compact definitions in FeatureFlagConfig.ts.
 * This file intentionally takes on the transformation work so the config stays
 * easy to scan and maintain while the rest of the codebase gets typed values.
 */

import { FAMA_FLAG_DEFINITIONS } from "./FeatureFlagConfig";
import type {
  BulkFeatureFlagValues,
  FamaFlagDefinition,
  FamaFlagValue,
} from "./FeatureFlagDefinition";

export type {
  BehaviorMappingActions,
  BulkFeatureFlagValues,
} from "./FeatureFlagDefinition";

export type FamaFlagsTypes = {
  [K in keyof typeof FAMA_FLAG_DEFINITIONS]: FamaFlagValue<
    (typeof FAMA_FLAG_DEFINITIONS)[K]
  >;
};

export type FamaFlagKey = keyof typeof FAMA_FLAG_DEFINITIONS;

const FAMA_FLAG_KEYS = Object.keys(FAMA_FLAG_DEFINITIONS) as FamaFlagKey[];

export const FAMA_FEATURE_FLAG_LD_KEYS: string[] = Object.values(
  FAMA_FLAG_DEFINITIONS
).map(({ ldKey }) => ldKey);

function getFlagDefinition<K extends FamaFlagKey>(
  appKey: K
): FamaFlagDefinition<FamaFlagsTypes[K]> {
  return FAMA_FLAG_DEFINITIONS[appKey] as unknown as FamaFlagDefinition<
    FamaFlagsTypes[K]
  >;
}

function resolveFlagValue<K extends FamaFlagKey>(
  appKey: K,
  value: unknown,
  fallback: FamaFlagsTypes[K]
): FamaFlagsTypes[K] {
  return getFlagDefinition(appKey).normalize(value, fallback);
}

function getBulkValue(
  values: BulkFeatureFlagValues | undefined,
  ldKey: string
): unknown {
  if (!values) return undefined;
  return values[ldKey];
}

export function resolveFamaFlags(
  bulkValues: BulkFeatureFlagValues | undefined
): FamaFlagsTypes {
  const resolved = {} as Record<FamaFlagKey, FamaFlagsTypes[FamaFlagKey]>;

  for (const appKey of FAMA_FLAG_KEYS) {
    const definition = getFlagDefinition(appKey);
    const bulkValue = getBulkValue(bulkValues, definition.ldKey);
    resolved[appKey] = (
      bulkValue !== undefined
        ? resolveFlagValue(appKey, bulkValue, definition.default)
        : definition.default
    ) as FamaFlagsTypes[FamaFlagKey];
  }

  return resolved as FamaFlagsTypes;
}
